import { SiteHeader } from '@/components/SiteHeader.tsx';
import { getSessionToken } from '@/lib/session.ts';
import {
  GROUND_SPECIAL_ZONE_MAX,
  MVP_EVENT_ID,
  NON_ZONE_SEATS_MAX,
  type SeatDto,
  isGroundSpecialZoneSeat,
  reservationIsOpen,
} from '@okn/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutForm } from './CheckoutForm.tsx';
import { CheckoutPanel } from './CheckoutPanel.tsx';
import { ReservationOpening } from './ReservationOpening.tsx';
import { SeatMap } from './SeatMap/index.tsx';
import { type SelectedStudent, StudentGate } from './StudentGate.tsx';
import { useHoldManager } from './api/useHoldManager.ts';
import { useSeatmap } from './api/useSeatmap.ts';
import { useSeatmapState } from './api/useSeatmapState.ts';

// Gate the whole booking screen behind the opening time. Before it, show the
// countdown (which flips this to the booking flow automatically at open).
export function EventPage() {
  const [open, setOpen] = useState(() => reservationIsOpen());
  if (!open) return <ReservationOpening onOpen={() => setOpen(true)} />;
  return <BookingScreen />;
}

function BookingScreen() {
  const session = useMemo(() => getSessionToken(), []);
  const map = useSeatmap(MVP_EVENT_ID);
  const { state: dynState } = useSeatmapState(MVP_EVENT_ID, session);
  const holds = useHoldManager(MVP_EVENT_ID, session);
  const [showForm, setShowForm] = useState(false);

  // Student-first gate: the veli picks the öğrenci before choosing seats; their
  // contact details (phone/e-mail/note) are collected in the checkout modal.
  const [student, setStudent] = useState<SelectedStudent | null>(null);

  // Selection caps are per-reservation (no phone known up front). The per-phone
  // cumulative cap is re-checked server-side at reservation time.
  const maxSeats = map.status === 'ready' ? map.data.event.maxSeatsPerReservation : 7;
  const maxZone = GROUND_SPECIAL_ZONE_MAX;
  const maxNonZone = NON_ZONE_SEATS_MAX;

  useEffect(() => {
    if (dynState?.ownHeldSeatIds?.length && holds.selected.size === 0) {
      // Restore both the seats AND the original countdown so a reload brings
      // back the in-progress selection from the first hold.
      holds.restoreFromServer(dynState.ownHeldSeatIds, dynState.ownHoldExpiresAt ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynState?.ownHeldSeatIds?.length]);

  const stateCtx = useMemo(
    () => ({
      reservedSeatIds: new Set(dynState?.reservedSeatIds ?? []),
      soldSeatIds: new Set(dynState?.soldSeatIds ?? []),
      heldSeatIds: new Set(
        (dynState?.heldSeatIds ?? []).filter(
          (id) => !(dynState?.ownHeldSeatIds ?? []).includes(id),
        ),
      ),
    }),
    [dynState],
  );

  const selectedSeats = useMemo<SeatDto[]>(() => {
    if (map.status !== 'ready') return [];
    return map.data.allSeats.filter((s) => holds.selected.has(s.id));
  }, [map, holds.selected]);

  const hasSelection = selectedSeats.length > 0;

  // "Özel alan" cap — a customer may pick at most GROUND_SPECIAL_ZONE_MAX seats
  // from the hatched ground zone. Mirrored server-side in the holds route.
  const zoneSelectedCount = useMemo(
    () =>
      selectedSeats.filter(
        (s) => s.level === 'ground' && isGroundSpecialZoneSeat(s.rowLabel, s.seatNumber),
      ).length,
    [selectedSeats],
  );

  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<number | null>(null);
  const flashNotice = (msg: string) => {
    setNotice(msg);
    if (noticeTimer.current != null) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 2600);
  };

  const resetStudent = () => {
    holds.clearAll();
    setStudent(null);
    setShowForm(false);
  };

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <SiteHeader />

      {/* Editorial title block — compact so header + title + seat map all fit on one screen */}
      <section className="relative shrink-0 border-b border-border px-8 py-4">
        <div className="mx-auto max-w-[1680px]">
          <div className="flex flex-wrap items-center justify-between gap-x-10 gap-y-3">
            <div className="min-w-0 flex items-baseline gap-5">
              <h1 className="display text-[clamp(24px,2.6vw,38px)] leading-[1] font-light tracking-tight">
                {map.status === 'ready' ? (
                  <EventTitle name={map.data.event.name} />
                ) : (
                  <span className="text-text-muted">Yükleniyor…</span>
                )}
              </h1>
              {map.status === 'ready' && (
                <p className="text-[12px] text-text-muted tnum hidden md:block">
                  {formatStartsAt(map.data.event.startsAt)}
                </p>
              )}
            </div>

            <div className="flex items-center gap-5 text-[10px] uppercase tracking-[0.22em] text-text-faint">
              {student && (
                <>
                  <div className="flex items-center gap-2">
                    <span>Öğrenci</span>
                    <button
                      type="button"
                      onClick={resetStudent}
                      className="ml-1 rounded border border-border px-1.5 py-0.5 text-[9px] normal-case tracking-normal text-text-muted transition-colors hover:border-border-strong hover:text-text"
                    >
                      {student.name} · değiştir
                    </button>
                  </div>
                  <div className="hidden sm:block h-6 w-px bg-border-strong" />
                  <div className="hidden sm:flex items-center gap-2">
                    <span>Hak</span>
                    <span className="text-text tnum">
                      en fazla {maxSeats} koltuk · özel {maxZone}
                    </span>
                  </div>
                  <div className="hidden sm:block h-6 w-px bg-border-strong" />
                </>
              )}
              <div className="hidden sm:flex items-center gap-2">
                <span>Onay</span>
                <span className="text-text">19 Haz 19.30 · Akademide</span>
              </div>
              <div className="hidden sm:block h-6 w-px bg-border-strong" />
              <div className="flex items-center gap-2">
                <span>Hold</span>
                <span className="text-text tnum">
                  {map.status === 'ready' ? `${map.data.event.reserveHoldMinutes}:00` : '5:00'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seat map — fills remaining viewport. When the checkout cart drawer is
          open it reserves 380px on the right so the right balcony stays clickable. */}
      <main
        className={`relative flex flex-1 flex-col px-6 py-4 min-h-0 transition-[padding] duration-300 ${
          hasSelection ? 'lg:pr-[396px]' : ''
        }`}
      >
        <div className="mx-auto flex w-full max-w-[1680px] flex-1 flex-col min-h-0">
          {map.status === 'loading' && (
            <div className="flex flex-1 items-center justify-center text-text-faint">
              <div className="display text-2xl italic">Salon hazırlanıyor…</div>
            </div>
          )}
          {map.status === 'error' && (
            <div className="flex flex-1 items-center justify-center text-danger">{map.message}</div>
          )}
          {map.status === 'ready' && (
            <SeatMap
              data={map.data}
              selectedIds={holds.selected}
              stateContext={stateCtx}
              onSelect={(seat) => {
                if (stateCtx.reservedSeatIds.has(seat.id)) return;
                if (stateCtx.soldSeatIds.has(seat.id)) return;
                if (stateCtx.heldSeatIds.has(seat.id)) return;
                if (!seat.isSellable || seat.kind !== 'standard') return;
                const adding = !holds.selected.has(seat.id);
                // Total cap reached — state the limit, never "X more".
                if (adding && selectedSeats.length >= maxSeats) {
                  flashNotice(`En fazla ${maxSeats} koltuk seçebilirsiniz.`);
                  return;
                }
                const isZoneSeat =
                  seat.level === 'ground' &&
                  isGroundSpecialZoneSeat(seat.rowLabel, seat.seatNumber);
                // Özel alan (VIP) cap.
                if (adding && isZoneSeat && zoneSelectedCount >= maxZone) {
                  flashNotice(`Özel alandan en fazla ${maxZone} koltuk seçebilirsiniz.`);
                  return;
                }
                // Özel alan dışı (non-VIP) cap — independent of the VIP cap.
                if (
                  adding &&
                  !isZoneSeat &&
                  selectedSeats.length - zoneSelectedCount >= maxNonZone
                ) {
                  flashNotice(`Özel alan dışından en fazla ${maxNonZone} koltuk seçebilirsiniz.`);
                  return;
                }
                holds.toggle(seat.id);
              }}
            />
          )}
        </div>
      </main>

      <CheckoutPanel
        selected={selectedSeats}
        expiresAt={holds.expiresAt}
        errorMessage={holds.error?.message ?? null}
        onRemove={(id) => holds.toggle(id)}
        onClearAll={holds.clearAll}
        onCheckout={() => setShowForm(true)}
      />

      {!student && <StudentGate eventId={MVP_EVENT_ID} onConfirm={(s) => setStudent(s)} />}

      {showForm && hasSelection && student && (
        <CheckoutForm
          selected={selectedSeats}
          studentId={student.id}
          studentName={student.name}
          onChangeStudent={resetStudent}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            holds.restoreFromServer([]);
          }}
        />
      )}

      {notice && (
        <div
          role="status"
          className="pointer-events-none fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-lg border border-accent/40 bg-bg-elevated px-4 py-2.5 text-[13px] text-text shadow-lg"
        >
          {notice}
        </div>
      )}
    </div>
  );
}

function formatStartsAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('tr-TR', {
      timeZone: 'Europe/Istanbul',
      dateStyle: 'full',
      timeStyle: 'short',
    })
      .format(new Date(iso))
      .replace(/(\d{1,2}):(\d{2})/g, '$1.$2');
  } catch {
    return iso;
  }
}

// Splits the event name into a head + italic tail when there's a space,
// so "Sezon Konseri" reads as Sezon *Konseri* — editorial typography trick.
function EventTitle({ name }: { name: string }) {
  const trimmed = name.trim();
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace <= 0) {
    return <em className="italic text-accent font-normal">{trimmed}</em>;
  }
  const head = trimmed.slice(0, lastSpace);
  const tail = trimmed.slice(lastSpace + 1);
  return (
    <>
      {head} <em className="italic text-accent font-normal">{tail}</em>
    </>
  );
}
